/**
 * Institution Admin Console — BRD §5, §7.2.
 *
 * Platform-admin only. Full lifecycle: list, view, create, edit tenant,
 * edit tenant configuration. This screen replaces the earlier
 * PlannedFeaturePage stub for /institutions.
 *
 * Data flow:
 *   GET  /api/admin/tenants                       → list
 *   POST /api/admin/tenants                       → create
 *   GET  /api/admin/tenants/:id                   → detail
 *   PATCH /api/admin/tenants/:id                  → update
 *   GET  /api/admin/tenants/:id/configuration     → config
 *   PUT  /api/admin/tenants/:id/configuration     → save config
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Building2, Plus, Search, ArrowLeft, Save, X,
  Landmark, Dice5, ShieldCheck, Signal, CreditCard, MoreHorizontal,
} from "lucide-react";
import api, { setAssumedTenant } from "@/lib/api";

/** Extract a human-readable error message from an axios error.
 * FastAPI 422 responses ship `detail` as an array of ValidationError dicts —
 * passing that straight into sonner's toast.error crashes the render tree
 * with "Objects are not valid as a React child". Coerce here. */
function errMessage(err, fallback = "Request failed") {
  const d = err?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map((e) => {
      const loc = Array.isArray(e?.loc) ? e.loc.filter((p) => p !== "body").join(".") : "";
      const msg = e?.msg || e?.type || JSON.stringify(e);
      return loc ? `${loc}: ${msg}` : msg;
    }).join("; ");
  }
  return JSON.stringify(d);
}

/** Remove empty-string values from an object so optional EmailStr / etc.
 * on the backend don't reject them with 422. */
function stripEmpty(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === "" || v === null || v === undefined) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    out[k] = v;
  }
  return out;
}

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

const INDUSTRY_LABEL = {
  financial_services: { label: "Financial services", icon: Landmark, color: "#1E40AF" },
  gaming:             { label: "Gaming",             icon: Dice5,   color: "#C9A24A" },
  insurance:          { label: "Insurance",          icon: ShieldCheck, color: "#065F46" },
  telecoms:           { label: "Telecoms",           icon: Signal,  color: "#7C3AED" },
  credit_provider:    { label: "Credit provider",    icon: CreditCard, color: "#B45309" },
  other:              { label: "Other",              icon: MoreHorizontal, color: "#64748B" },
};

const STATUS_TONE = {
  draft:     { bg: "#F1F5F9", color: "#334155", label: "Draft" },
  active:    { bg: "#D1FAE5", color: "#065F46", label: "Active" },
  suspended: { bg: "#FEE2E2", color: "#7F1D1D", label: "Suspended" },
};

export default function InstitutionsConsole() {
  const [tenants, setTenants] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/tenants");
    setTenants(data || []);
  };

  useEffect(() => { load(); }, []);

  if (selectedId) {
    return (
      <TenantDetail
        tenantId={selectedId}
        onBack={() => { setSelectedId(null); load(); }}
      />
    );
  }

  const filtered = (tenants || []).filter((t) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (t.trading_name || t.name || "").toLowerCase().includes(q) ||
      (t.legal_name || "").toLowerCase().includes(q) ||
      (t.tenant_id || "").toLowerCase().includes(q) ||
      (t.industry_vertical || t.sector || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="max-w-6xl mx-auto p-6 lg:p-10 space-y-6" data-testid="institutions-console">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>
            Trust Utility · Tenants
          </div>
          <h1 className="font-display text-3xl font-semibold" style={{ color: NAVY }}>Institutions</h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">
            Every institution on Altara is a Tenant with its own org ID, industry vertical, configuration, and API keys.
            Zero cross-contamination: institution data is repository-filtered by <code>tenant_id</code>.
          </p>
        </div>
        <button
          data-testid="new-tenant-btn"
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white"
          style={{ background: NAVY }}
        >
          <Plus className="w-4 h-4" /> New institution
        </button>
      </header>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="tenant-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, ID, industry…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm"
          />
        </div>
        <span className="text-[11px] text-slate-500">{filtered.length} of {tenants?.length ?? 0} tenants</span>
      </div>

      <TenantTable tenants={filtered} loading={tenants === null} onOpen={setSelectedId} />

      {showCreate && (
        <CreateTenantDialog
          onClose={() => setShowCreate(false)}
          onCreated={(t) => { setShowCreate(false); load().then(() => setSelectedId(t.tenant_id)); }}
        />
      )}
    </div>
  );
}

function TenantTable({ tenants, loading, onOpen }) {
  if (loading) {
    return <div className="text-sm text-slate-500 p-6">Loading…</div>;
  }
  if (!tenants.length) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center">
        <Building2 className="w-8 h-8 mx-auto text-slate-300" />
        <div className="text-sm text-slate-600 mt-2">No tenants match.</div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-white border border-slate-100 shadow-sm overflow-hidden">
      <table className="w-full text-sm" data-testid="tenant-table">
        <thead>
          <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100 bg-slate-50">
            <th className="text-left px-4 py-2.5">Institution</th>
            <th className="text-left px-4 py-2.5">Industry</th>
            <th className="text-left px-4 py-2.5">Status</th>
            <th className="text-left px-4 py-2.5">Tenant ID</th>
            <th className="text-left px-4 py-2.5"></th>
          </tr>
        </thead>
        <tbody>
          {tenants.map((t) => {
            const industry = INDUSTRY_LABEL[t.industry_vertical] || INDUSTRY_LABEL.other;
            const status = STATUS_TONE[t.status] || STATUS_TONE.draft;
            const Icon = industry.icon;
            return (
              <tr
                key={t.tenant_id}
                data-testid={`tenant-row-${t.tenant_id}`}
                onClick={() => onOpen(t.tenant_id)}
                className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
              >
                <td className="px-4 py-3">
                  <div className="font-semibold" style={{ color: NAVY }}>{t.trading_name || t.name || "—"}</div>
                  {t.legal_name && t.legal_name !== (t.trading_name || t.name) && (
                    <div className="text-[11px] text-slate-500">{t.legal_name}</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: industry.color }}>
                    <Icon className="w-3.5 h-3.5" /> {industry.label}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded font-semibold" style={{ background: status.bg, color: status.color }}>
                    {status.label}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-[11px] text-slate-500">{t.tenant_id}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    data-testid={`view-as-${t.tenant_id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setAssumedTenant(t.tenant_id);
                      toast.success(`Viewing platform as ${t.trading_name || t.name}`);
                      setTimeout(() => window.location.reload(), 200);
                    }}
                    className="text-[11px] font-semibold text-slate-500 hover:text-slate-800"
                  >
                    View as →
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ─── Create dialog ────────────────────────────────────────── */

const EMPTY_CREATE = {
  legal_name: "",
  trading_name: "",
  industry_vertical: "financial_services",
  registration_number: "",
  regulator_reference: "",
  primary_contact_name: "",
  primary_contact_email: "",
  primary_contact_phone: "",
  staff_email_domains: "",
  status: "draft",
};

function CreateTenantDialog({ onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY_CREATE);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = stripEmpty({
        ...form,
        staff_email_domains: form.staff_email_domains
          .split(",").map((s) => s.trim()).filter(Boolean),
      });
      const { data } = await api.post("/admin/tenants", payload);
      toast.success(`${data.trading_name || data.name} created`);
      onCreated(data);
    } catch (err) {
      toast.error(errMessage(err, "Could not create tenant"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        data-testid="create-tenant-dialog"
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest" style={{ color: GOLD }}>New institution</div>
            <h2 className="font-display text-xl font-semibold" style={{ color: NAVY }}>Onboard a tenant</h2>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-slate-100 text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
          <Field label="Legal name" testid="c-legal-name" required value={form.legal_name} onChange={(v) => setForm({ ...form, legal_name: v })} />
          <Field label="Trading name" testid="c-trading-name" value={form.trading_name} onChange={(v) => setForm({ ...form, trading_name: v })} />
          <Select label="Industry vertical" testid="c-industry" value={form.industry_vertical}
            onChange={(v) => setForm({ ...form, industry_vertical: v })}
            options={Object.entries(INDUSTRY_LABEL).map(([v, m]) => ({ value: v, label: m.label }))} />
          <Select label="Status" testid="c-status" value={form.status}
            onChange={(v) => setForm({ ...form, status: v })}
            options={[{value:"draft",label:"Draft"},{value:"active",label:"Active"},{value:"suspended",label:"Suspended"}]} />
          <Field label="Registration number" testid="c-reg-no" value={form.registration_number} onChange={(v) => setForm({ ...form, registration_number: v })} />
          <Field label="Regulator reference" testid="c-reg-ref" value={form.regulator_reference} onChange={(v) => setForm({ ...form, regulator_reference: v })} placeholder="e.g. FSCA FSP number" />
          <Field label="Primary contact name" testid="c-contact-name" value={form.primary_contact_name} onChange={(v) => setForm({ ...form, primary_contact_name: v })} />
          <Field label="Primary contact email" testid="c-contact-email" type="email" value={form.primary_contact_email} onChange={(v) => setForm({ ...form, primary_contact_email: v })} />
          <Field label="Primary contact phone" testid="c-contact-phone" value={form.primary_contact_phone} onChange={(v) => setForm({ ...form, primary_contact_phone: v })} />
          <Field label="Staff email domains (comma separated)" testid="c-domains" value={form.staff_email_domains} onChange={(v) => setForm({ ...form, staff_email_domains: v })} placeholder="novabank.co.za, novabank.com" full />
        </div>
        <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="text-[13px] font-semibold px-3 py-1.5 text-slate-600">Cancel</button>
          <button
            type="submit"
            disabled={saving}
            data-testid="create-tenant-submit"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold px-4 py-1.5 rounded text-white disabled:opacity-50"
            style={{ background: NAVY }}
          >
            <Save className="w-3.5 h-3.5" /> Create tenant
          </button>
        </div>
      </form>
    </div>
  );
}

/* ─── Detail view ─────────────────────────────────────────── */

function TenantDetail({ tenantId, onBack }) {
  const [tenant, setTenant] = useState(null);
  const [config, setConfig] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  const reload = async () => {
    const [{ data: t }, { data: c }] = await Promise.all([
      api.get(`/admin/tenants/${tenantId}`),
      api.get(`/admin/tenants/${tenantId}/configuration`),
    ]);
    setTenant(t); setConfig(c);
  };

  useEffect(() => { reload(); }, [tenantId]);

  if (!tenant || !config) {
    return <div className="text-sm text-slate-500 p-6">Loading…</div>;
  }

  const industry = INDUSTRY_LABEL[tenant.industry_vertical] || INDUSTRY_LABEL.other;
  const IconIndustry = industry.icon;

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const patch = {
        legal_name: tenant.legal_name,
        trading_name: tenant.trading_name,
        industry_vertical: tenant.industry_vertical,
        registration_number: tenant.registration_number,
        regulator_reference: tenant.regulator_reference,
        primary_contact_name: tenant.primary_contact_name,
        primary_contact_email: tenant.primary_contact_email || null,
        primary_contact_phone: tenant.primary_contact_phone,
        staff_email_domains: tenant.staff_email_domains,
        status: tenant.status,
      };
      const { data } = await api.patch(`/admin/tenants/${tenantId}`, patch);
      setTenant(data);
      toast.success("Institution updated");
    } catch (err) {
      toast.error(errMessage(err, "Update failed"));
    } finally {
      setSavingProfile(false);
    }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const patch = {
        kyc_provider: config.kyc_provider,
        kyc_valid_days: config.kyc_valid_days,
        consent_default_expiry_days: config.consent_default_expiry_days,
        consent_max_expiry_days: config.consent_max_expiry_days,
        document_requirements: config.document_requirements,
        affordability_rules: config.affordability_rules,
        responsible_gaming_thresholds: config.responsible_gaming_thresholds,
        education_requirements: config.education_requirements,
        intervention_rules: config.intervention_rules,
      };
      const { data } = await api.put(`/admin/tenants/${tenantId}/configuration`, patch);
      setConfig(data);
      toast.success("Configuration saved");
    } catch (err) {
      toast.error(errMessage(err, "Save failed"));
    } finally {
      setSavingConfig(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 lg:p-10 space-y-6" data-testid="tenant-detail">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> All institutions
      </button>

      {/* Header */}
      <section className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${industry.color}22`, color: industry.color }}>
              <IconIndustry className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: industry.color }}>{industry.label}</div>
              <h1 className="font-display text-2xl font-semibold" style={{ color: NAVY }} data-testid="tenant-display-name">
                {tenant.trading_name || tenant.name}
              </h1>
              <div className="text-[11px] text-slate-500 font-mono mt-1">{tenant.tenant_id}</div>
            </div>
          </div>
          <button
            data-testid="detail-view-as-btn"
            onClick={() => {
              setAssumedTenant(tenant.tenant_id);
              toast.success(`Viewing platform as ${tenant.trading_name || tenant.name}`);
              setTimeout(() => window.location.reload(), 200);
            }}
            className="text-[13px] font-semibold rounded-full px-4 py-2 border border-slate-200 hover:bg-slate-50"
            style={{ color: NAVY }}
          >
            View platform as this tenant →
          </button>
        </div>
      </section>

      {/* Profile */}
      <Card title="Institution profile" testid="profile-card">
        <Grid>
          <Field label="Legal name" testid="d-legal-name" value={tenant.legal_name || ""} onChange={(v) => setTenant({ ...tenant, legal_name: v })} />
          <Field label="Trading name" testid="d-trading-name" value={tenant.trading_name || ""} onChange={(v) => setTenant({ ...tenant, trading_name: v })} />
          <Select label="Industry vertical" testid="d-industry" value={tenant.industry_vertical} onChange={(v) => setTenant({ ...tenant, industry_vertical: v })}
            options={Object.entries(INDUSTRY_LABEL).map(([v, m]) => ({ value: v, label: m.label }))} />
          <Select label="Status" testid="d-status" value={tenant.status} onChange={(v) => setTenant({ ...tenant, status: v })}
            options={[{value:"draft",label:"Draft"},{value:"active",label:"Active"},{value:"suspended",label:"Suspended"}]} />
          <Field label="Registration number" testid="d-reg-no" value={tenant.registration_number || ""} onChange={(v) => setTenant({ ...tenant, registration_number: v })} />
          <Field label="Regulator reference" testid="d-reg-ref" value={tenant.regulator_reference || ""} onChange={(v) => setTenant({ ...tenant, regulator_reference: v })} />
          <Field label="Primary contact name" testid="d-contact-name" value={tenant.primary_contact_name || ""} onChange={(v) => setTenant({ ...tenant, primary_contact_name: v })} />
          <Field label="Primary contact email" testid="d-contact-email" value={tenant.primary_contact_email || ""} onChange={(v) => setTenant({ ...tenant, primary_contact_email: v })} type="email" />
          <Field label="Primary contact phone" testid="d-contact-phone" value={tenant.primary_contact_phone || ""} onChange={(v) => setTenant({ ...tenant, primary_contact_phone: v })} />
          <Field label="Staff email domains (comma separated)" testid="d-domains" full
            value={(tenant.staff_email_domains || []).join(", ")}
            onChange={(v) => setTenant({ ...tenant, staff_email_domains: v.split(",").map((s) => s.trim()).filter(Boolean) })} />
        </Grid>
        <SaveBar onSave={saveProfile} busy={savingProfile} testid="save-profile-btn" />
      </Card>

      {/* Configuration */}
      <Card title="Configuration" testid="config-card" subtitle="Institution-specific rules. Configuration, not code.">
        <SubSection label="KYC">
          <Grid>
            <Field label="KYC provider" testid="cfg-kyc-provider" value={config.kyc_provider} onChange={(v) => setConfig({ ...config, kyc_provider: v })} />
            <Field label="KYC valid days" testid="cfg-kyc-valid-days" type="number" value={config.kyc_valid_days} onChange={(v) => setConfig({ ...config, kyc_valid_days: Number(v) })} />
          </Grid>
        </SubSection>
        <SubSection label="Consent expiry">
          <Grid>
            <Field label="Default expiry days" testid="cfg-consent-default" type="number" value={config.consent_default_expiry_days} onChange={(v) => setConfig({ ...config, consent_default_expiry_days: Number(v) })} />
            <Field label="Max expiry days" testid="cfg-consent-max" type="number" value={config.consent_max_expiry_days} onChange={(v) => setConfig({ ...config, consent_max_expiry_days: Number(v) })} />
          </Grid>
        </SubSection>
        <SubSection label="Document requirements">
          <JsonField testid="cfg-documents" value={config.document_requirements} onChange={(v) => setConfig({ ...config, document_requirements: v })} />
        </SubSection>
        <SubSection label="Affordability rules">
          <JsonField testid="cfg-affordability" value={config.affordability_rules} onChange={(v) => setConfig({ ...config, affordability_rules: v })} />
        </SubSection>
        <SubSection label="Responsible gaming thresholds (Phase 2 placeholder)">
          <JsonField testid="cfg-rg" value={config.responsible_gaming_thresholds} onChange={(v) => setConfig({ ...config, responsible_gaming_thresholds: v })} />
        </SubSection>
        <SubSection label="Education requirements">
          <JsonField testid="cfg-education" value={config.education_requirements} onChange={(v) => setConfig({ ...config, education_requirements: v })} />
        </SubSection>
        <SubSection label="Intervention rules (Phase 2 placeholder)">
          <JsonField testid="cfg-intervention" value={config.intervention_rules} onChange={(v) => setConfig({ ...config, intervention_rules: v })} />
        </SubSection>
        <SaveBar onSave={saveConfig} busy={savingConfig} testid="save-config-btn" />
      </Card>
    </div>
  );
}

/* ─── Small helpers ───────────────────────────────────────── */

function Card({ title, subtitle, testid, children }) {
  return (
    <section data-testid={testid} className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6 space-y-4">
      <div>
        <h2 className="font-display text-lg font-semibold" style={{ color: NAVY }}>{title}</h2>
        {subtitle && <div className="text-[12px] text-slate-500 mt-0.5">{subtitle}</div>}
      </div>
      {children}
    </section>
  );
}

function SubSection({ label, children }) {
  return (
    <div className="border-t border-slate-100 pt-3 first:border-t-0 first:pt-0">
      <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">{label}</div>
      {children}
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, testid, value, onChange, type = "text", placeholder, required, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}{required && " *"}</div>
      <input
        data-testid={testid}
        type={type} value={value ?? ""} placeholder={placeholder} required={required}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A1F44]/20 focus:border-[#0A1F44]"
      />
    </label>
  );
}

function Select({ label, testid, value, onChange, options }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <select
        data-testid={testid}
        value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function JsonField({ testid, value, onChange }) {
  const [text, setText] = useState(JSON.stringify(value ?? {}, null, 2));
  const [error, setError] = useState("");
  useEffect(() => { setText(JSON.stringify(value ?? {}, null, 2)); }, [value]);
  const handle = (v) => {
    setText(v);
    try {
      const parsed = JSON.parse(v || "{}");
      setError("");
      onChange(parsed);
    } catch { setError("Invalid JSON"); }
  };
  return (
    <div>
      <textarea
        data-testid={testid}
        value={text}
        onChange={(e) => handle(e.target.value)}
        rows={Math.min(10, Math.max(3, text.split("\n").length))}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-[12px] font-mono focus:outline-none focus:ring-2 focus:ring-[#0A1F44]/20 focus:border-[#0A1F44]"
      />
      {error && <div className="text-[11px] text-rose-600 mt-1">{error}</div>}
    </div>
  );
}

function SaveBar({ onSave, busy, testid }) {
  return (
    <div className="flex justify-end pt-2">
      <button
        onClick={onSave} disabled={busy}
        data-testid={testid}
        className="inline-flex items-center gap-1.5 text-[13px] font-semibold px-4 py-2 rounded text-white disabled:opacity-50"
        style={{ background: NAVY }}
      >
        <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save"}
      </button>
    </div>
  );
}
